import debounce from 'debounce-promise';
import isEmpty from 'lodash/isEmpty';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';

import LoadingArea from './LoadingArea';

const STATUS_LOADING = 1;
const DEFAULT_TOTAL = 10000;
const BATCH_SIZE = 50;
const THRESHOLD = 15;
const DEBOUNCE_THRESHOLD_MS = 300;
export const MAX_ROW_COUNT = 10000;

export default class WrappedInfiniteLoader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      rows: {},
      rowStatusMap: {},
      loading: false,
      hash: props.hash,
    };
  }

  UNSAFE_componentWillReceiveProps(props) {
    const { hash } = this.state;

    if (props.hash != hash) {
      this.reset(props.hash);
    }
  }

  isLoading = () => {
    const { loading, rows } = this.state;

    return loading && isEmpty(rows);
  };

  isEmpty = () => {
    const { total } = this.props;

    return total === 0;
  };

  reset = (hash) => {
    this.setState(
      {
        hash,
        rows: {},
        rowStatusMap: {},
      },
      () => {
        this.loadMoreRows({
          startIndex: 0,
          stopIndex: this.props.batchSize - 1,
        });
      },
    );
  };

  rowRenderer = ({ index, key, style }) => {
    const { rows } = this.state;
    const row = rows[index];

    return (
      <div key={key} style={style} index={index}>
        {row && this.props.rowRenderer(row, { index, key, style })}
      </div>
    );
  };

  isRowLoaded = ({ index }) => {
    const { rowStatusMap } = this.state;
    return !!rowStatusMap[index];
  };

  loadMoreRows = debounce(
    ({ startIndex, stopIndex }) => {
      let { rows, rowStatusMap } = this.state;

      for (var i = startIndex; i <= stopIndex; i++) {
        rowStatusMap = {
          ...rowStatusMap,
          [i]: STATUS_LOADING,
        };
      }

      this.setState({
        loading: true,
        rowStatusMap,
      });

      const promise = this.props.loadMoreRows({
        startIndex,
        stopIndex,
      });

      promise.then((results) => {
        for (var i = startIndex; i <= stopIndex; i++) {
          rowStatusMap = {
            ...rowStatusMap,
            [i]: STATUS_LOADING,
          };
        }

        results.forEach((item, i) => {
          rows = {
            ...rows,
            [i + startIndex]: item,
          };
        });

        this.setState({
          rowStatusMap,
          rows,
          loading: false,
        });
      });

      return promise;
    },
    DEBOUNCE_THRESHOLD_MS,
    { leading: true },
  );

  render() {
    const {
      rowHeight,
      total,
      defaultTotal,
      batchSize,
      threshold,
      emptyBody,
      listRef,
    } = this.props;

    const count = total || defaultTotal;

    return (
      <LoadingArea
        loading={this.isLoading()}
        emptyBody={emptyBody}
        empty={this.isEmpty()}>
        <InfiniteLoader
          isRowLoaded={this.isRowLoaded}
          loadMoreRows={this.loadMoreRows}
          rowCount={count}
          threshold={threshold}
          minimumBatchSize={batchSize}>
          {({ onRowsRendered, registerChild }) => (
            <AutoSizer>
              {({ height, width }) => (
                <List
                  ref={listRef || registerChild}
                  width={width}
                  height={height}
                  onRowsRendered={onRowsRendered}
                  rowCount={Math.min(count, MAX_ROW_COUNT)}
                  rowRenderer={this.rowRenderer}
                  rowHeight={rowHeight}
                />
              )}
            </AutoSizer>
          )}
        </InfiniteLoader>
      </LoadingArea>
    );
  }
}

WrappedInfiniteLoader.defaultProps = {
  rowHeight: 30,
  threshold: THRESHOLD,
  batchSize: BATCH_SIZE,
  defaultTotal: DEFAULT_TOTAL,
};

WrappedInfiniteLoader.propTypes = {
  batchSize: PropTypes.number,
  threshold: PropTypes.number,
  hash: PropTypes.any.isRequired,
  loadMoreRows: PropTypes.func.isRequired,
  rowRenderer: PropTypes.func.isRequired,
  total: PropTypes.number,
  emptyBody: PropTypes.node,
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  listRef: PropTypes.object,
  defaultTotal: PropTypes.number,
};
